import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getQueueSnapshot,
  startQueue,
  subscribeToQueue,
  type QueueSnapshot,
} from './queueStore.ts';

/**
 * Reads the offline queue and makes sure it is draining.
 *
 * All the state lives in the store, so this hook only subscribes — no effect writes
 * component state, and several components can use it without each running a flush loop.
 *
 * There is deliberately nothing here to call after a mutation. It used to hand back a
 * `refresh`, and every mutation site fired it on a zero-delay timer at the moment of the
 * tap — which raced the request whose failure does the enqueuing and beat it every time,
 * leaving the screen one intent behind. The queue announces itself instead; see
 * `onQueueChanged`.
 */
export function useOfflineQueue(): QueueSnapshot {
  const queryClient = useQueryClient();

  const snapshot = useSyncExternalStore(subscribeToQueue, getQueueSnapshot, getQueueSnapshot);

  // Attaching connectivity listeners is exactly the "synchronise with an external
  // system" job an effect is for. startQueue is idempotent.
  useEffect(() => startQueue(queryClient), [queryClient]);

  return snapshot;
}
